import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Link, useNavigate } from 'react-router-dom';
import { makeRequest2 } from '../../../../utils/makeRequest';
import { toastify } from '../../../../utils/toast';
import { getItem } from '../../../../utils/localStorage';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';

const DashboardTab = () => {
  // State for data
  const [loading, setLoading] = useState(false);
  const [rfqs, setRfqs] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [rfqSummary, setRfqSummary] = useState([
    { status: 'Active', count: 0, icon: 'check', color: 'success' },
    { status: 'Closed', count: 0, icon: 'archive', color: 'info' }
  ]);

  // Modal state
  const [displayModal, setDisplayModal] = useState(false);
  const [selectedRfq, setSelectedRfq] = useState(null);
  const [loadingRfqDetails, setLoadingRfqDetails] = useState(false);

  const navigate = useNavigate();

  // Fetch current user data when component mounts
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const currentUserValue = await getItem("APPUSER");
        let parsedUser;

        // Check if the value needs to be parsed
        if (typeof currentUserValue === "string") {
          try {
            parsedUser = JSON.parse(currentUserValue);
          } catch (err) {
            console.error("Error parsing APPUSER:", err);
          }
        } else {
          parsedUser = currentUserValue; // Assume it's already an object
        }

        // Safely access and set user details
        if (parsedUser?.user) {
          setCurrentUser(parsedUser.user);
        } else {
          console.error("Invalid user structure:", parsedUser);
        }
      } catch (err) {
        console.log(err.message);
      }
    };

    getCurrentUser();
  }, []);

  // Fetch supplier RFQs when user data is available
  useEffect(() => {
    if (currentUser && currentUser.userId) {
      fetchSupplierRfqs(currentUser.userId);
    }
  }, [currentUser]);

  // Function to fetch RFQs for the current supplier
  const fetchSupplierRfqs = async (supplierId) => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/app/quotations/suppliers/${supplierId}`,
        "GET"
      );

      if (response.success && response.data) {
        // Check if data is an array directly or if it's within response.data.data
        const rfqData = Array.isArray(response.data) ? response.data : response.data.data;

        if (Array.isArray(rfqData) && rfqData.length > 0) {
          const filteredRfqs = rfqData.filter(
            rfq =>
              rfq.approvalStatus !== 'pending' &&
              rfq.approvalStatus !== 'in_progress' &&
              rfq.approvalStatus !== 'rejected'
          );

          setRfqs(filteredRfqs);

          // Update summary counts - remove "Pending" category
          const summary = [...rfqSummary];
          const activeCount = filteredRfqs.filter(rfq => rfq.status === 'Active').length;
          const closedCount = filteredRfqs.filter(rfq => rfq.status === 'Closed').length;

          summary[0].count = activeCount;
          summary[1].count = closedCount;

          setRfqSummary(summary);
        } else {
          setRfqs([]);
          toastify("No RFQs found in the data", "info");
        }
      } else {
        setRfqs([]);
        toastify("No RFQs found or error fetching data", "info");
      }
    } catch (error) {
      toastify(error.message || "Error fetching supplier RFQs", "error");
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch RFQ details by ID
  const fetchRfqDetails = async (rfqId) => {
    try {
      setLoadingRfqDetails(true);
      const response = await makeRequest2(`/api/app/quotations/by_id/${rfqId}`, "GET");

      if (response.success && response.data.data) {
        setSelectedRfq(response.data.data);
        setDisplayModal(true);
      } else {
        toastify("Error fetching RFQ details", "error");
      }
    } catch (error) {
      toastify(error.message || "Error fetching RFQ details", "error");
    } finally {
      setLoadingRfqDetails(false);
    }
  };

  // Function to navigate to the response screen
  const navigateToResponse = (rfqId) => {
    navigate(`/facility/supplier_rfq_management/tabs/add_rfq_response/${rfqId}`);
  };

  const statusTemplate = (rowData) => {
    const statusStyles = {
      Active: { color: '#28a745', backgroundColor: '#e9fbe9' },
      Closed: { color: '#6c757d', backgroundColor: '#f1f1f1' }
    };

    const style = statusStyles[rowData.status] || {
      color: '#6c757d',
      backgroundColor: '#f1f1f1'
    };

    return (
      <span
        style={{
          ...style,
          padding: '0.5em 1em',
          borderRadius: '12px',
          fontWeight: 'bold',
          fontSize: '0.9rem',
          display: 'inline-block'
        }}
      >
        {rowData.status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const openingDateTemplate = (rowData) => {
    return formatDate(rowData.startDate);
  };

  const closingDateTemplate = (rowData) => {
    return formatDate(rowData.closingDate);
  };

  const rfqFeeTemplate = (rowData) => {
    if (rowData.rfqFee === 0 || rowData.rfqFee === undefined) {
      return 'Free';
    }

    return `${rowData.currency || ''} ${rowData.rfqFee?.toFixed(2) || 0}`;
  };

  const facilityTemplate = (rowData) => {
    // Facility information might need to be fetched or transformed
    return rowData.facilityName || rowData.facilityId || 'N/A';
  };

  const actionTemplate = (rowData) => {
    return (
      <div className="d-flex gap-2 justify-content-center">
        <Button
          icon="ti ti-eye"
          className="p-button-text p-button-rounded p-button-info"
          tooltip="View RFQ"
          onClick={() => fetchRfqDetails(rowData._id)}
        />
        {rowData.status !== 'Closed' && (
          <Button
            icon="ti ti-send"
            className="p-button-text p-button-rounded p-button-success"
            tooltip="Send Quotation"
            onClick={() => navigateToResponse(rowData._id)}
          />
        )}
      </div>
    );
  };


  const categoryTemplate = (rowData) => {
    return rowData.category || rowData.rfqType || 'N/A';
  };

  const approvalStatusTemplate = (rowData) => {
    const statusStyles = {
      approved: { color: '#28a745', backgroundColor: '#e9fbe9' },
      in_progress: { color: '#007bff', backgroundColor: '#e6f3ff' },
      rejected: { color: '#dc3545', backgroundColor: '#fbe9e9' }
    };

    const style = statusStyles[rowData.approvalStatus] || {
      color: '#6c757d',
      backgroundColor: '#f1f1f1'
    };

    return (
      <span
        style={{
          ...style,
          padding: '0.5em 1em',
          borderRadius: '12px',
          fontWeight: 'bold',
          fontSize: '0.9rem',
          display: 'inline-block'
        }}
      >
        {rowData.approvalStatus === 'in_progress' ? 'In Progress' :
          rowData.approvalStatus.charAt(0).toUpperCase() + rowData.approvalStatus.slice(1)}
      </span>
    );
  };

  // Modal footer with close button
  const modalFooter = (
    <div>
      <Button
        type="button"
        className="btn btn-outline-secondary"
        onClick={() => setDisplayModal(false)}
      >
        Close
      </Button>
      &nbsp;
      {selectedRfq?.status !== 'Closed' && (
        <Button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setDisplayModal(false);
            if (selectedRfq) {
              navigateToResponse(selectedRfq._id);
            }
          }}
        >
          Send Quotation
        </Button>
      )}
    </div>
  );

  return (
    <div className="row">
      <div className="col-md-12 mb-4">
        <div className="card">
          <div className="card-header">
            <h4 className="mb-0">RFQ Summary</h4>
          </div>
          <div className="card-body">
            <div className="row">
              {rfqSummary.map((item, index) => (
                <div className="col-md-6" key={index}>
                  <div className="card mb-0 border-0">
                    <div className="card-body d-flex align-items-center">
                      <div className={`avtar avtar-lg bg-light-${item.color} me-3`}>
                        <i className={`ti ti-${item.icon}`}></i>
                      </div>
                      <div>
                        <h3 className="mb-1">{item.count}</h3>
                        <p className="mb-0">{item.status} RFQs</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="col-md-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="mb-0">My RFQs</h4>
            <div>
              <button className="btn btn-sm btn-primary">
                <i className="ti ti-filter me-1"></i>Filter
              </button>
            </div>
          </div>
          <div className="card-body">
            <DataTable
              value={rfqs}
              emptyMessage="No approved RFQs found for your account."
              paginator
              rows={5}
              rowsPerPageOptions={[5, 10, 25]}
              stripedRows
              loading={loading}
              tableStyle={{ minWidth: '100%' }}
              responsiveLayout="scroll"
            >
              <Column field="rfqNumber" header="RFQ Number" sortable />
              <Column field="name" header="RFQ Name" sortable />
              <Column header="Category" body={categoryTemplate} sortable field="category" />
              <Column header="RFQ Fee" body={rfqFeeTemplate} sortable field="rfqFee" />
              <Column header="Status" body={statusTemplate} sortable field="status" />
              <Column header="Opening Date" body={openingDateTemplate} sortable field="startDate" />
              <Column header="Closing Date" body={closingDateTemplate} sortable field="closingDate" />
              <Column header="Facility" body={facilityTemplate} sortable field="facilityName" />
              <Column header="Actions" body={actionTemplate} style={{ width: '150px' }} />
            </DataTable>
          </div>
        </div>
      </div>

      {/* RFQ Details Modal */}
      <Dialog
        header="RFQ Details"
        visible={displayModal}
        style={{ width: '70vw' }}
        footer={modalFooter}
        onHide={() => setDisplayModal(false)}
        closable
        maximizable
      >
        {loadingRfqDetails && (
          <div className="d-flex justify-content-center">
            <i className="pi pi-spinner pi-spin" style={{ fontSize: '2rem' }}></i>
          </div>
        )}

        {!loadingRfqDetails && selectedRfq && (
          <div className="rfq-details">
            <div className="row mb-3">
              <div className="col-md-6">
                <h5 className="mb-1">RFQ Number</h5>
                <p>{selectedRfq.rfqNumber}</p>
              </div>
              <div className="col-md-6">
                <h5 className="mb-1">RFQ Name</h5>
                <p>{selectedRfq.name}</p>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-6">
                <h5 className="mb-1">Opening Date</h5>
                <p>{formatDate(selectedRfq.startDate)}</p>
              </div>
              <div className="col-md-6">
                <h5 className="mb-1">Closing Date</h5>
                <p>{formatDate(selectedRfq.closingDate)}</p>
              </div>
            </div>

            {selectedRfq.items && selectedRfq.items.length > 0 && (
              <div className="row mb-3">
                <div className="col-12">
                  <h5 className="mb-3">Items</h5>
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Description</th>
                          <th>Quantity</th>
                          <th>Unit</th>
                          <th>Specifications</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedRfq.items.map((item, index) => (
                          <tr key={index}>
                            <td>{item.itemName}</td>
                            <td>{item.description || 'N/A'}</td>
                            <td>{item.quantity}</td>
                            <td>{item.unitOfMeasure}</td>
                            <td>{item.specifications || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default DashboardTab;